/**
 * INIKRU — SEO landing page content registry.
 *
 * One entry per indexable marketing route. `SeoLandingPage.js` renders any of
 * them, so ADDING A PAGE IS ONE ENTRY HERE plus one line in App.js.
 *
 * ⚠️ THE PATHS HERE MUST MATCH `fitcraft_designer_site/apex_seo.py` ROUTES.
 * That Python registry drives the <title>, description, canonical, sitemap and
 * robots.txt; this one drives what the page actually SAYS. A path in the sitemap
 * with no route here renders the SPA's catch-all — Google would be invited to
 * crawl a page that does not exist, and would index the HTTP 200 it gets back
 * as a soft 404. `python manage.py check_seo_routes` fails if the two lists
 * disagree, or if a `related:` link points at a route that does not exist.
 *
 * WRITING RULE: the target keywords have to appear in the VISIBLE COPY to matter
 * — a meta keywords tag does nothing. But copy written to hit a keyword list
 * reads like it, and Google has been demoting that since well before this site
 * existed. So every line here is written to be useful to a shop owner first; the
 * keywords are present because they are genuinely what the product does.
 */

export const SEO_PAGES = {
  '/ecommerce-platform': {
    eyebrow: 'Ecommerce platform',
    h1: 'One ecommerce platform to run the whole business',
    lede:
      'Most ecommerce software gives you a shop window and stops there. Inikru is a D2C ' +
      'platform: the branded storefront, plus the inventory, orders, GST invoicing, ' +
      'payments and customer records that keep it running after the first sale.',
    hue: 'store',
    features: [
      {
        title: 'A storefront that is yours',
        body:
          'Your own domain, your own design, your own customer list. Not a stall inside ' +
          'someone else’s marketplace, and not a template every other shop is using.',
      },
      {
        title: 'Stock that matches the shelf',
        body:
          'Inventory updates as orders come in — across sizes, colours and locations. ' +
          'The number on the website is the number in the room.',
      },
      {
        title: 'Billing that is already compliant',
        body:
          'GST invoices, tax invoices with GSTIN and credit notes generate from orders ' +
          'you already have. Nothing gets typed into a second system.',
      },
      {
        title: 'Payments that settle to you',
        body:
          'Take UPI, cards and netbanking through your own payment gateway account. ' +
          'The money goes to your bank, not into an escrow you have to chase.',
      },
    ],
    sections: [
      {
        title: 'Why "all-in-one" is not a marketing word here',
        body:
          'Running an online business on four disconnected tools is how stock goes wrong. ' +
          'The website says one thing, the stock register says another, the accountant asks ' +
          'for a third number. Because Inikru’s ecommerce software, inventory control and ' +
          'order fulfilment share one database, there is only ever one number to disagree with.',
      },
      {
        title: 'Built for how Indian businesses actually sell',
        body:
          'Orders arrive from a website, a WhatsApp message, an Instagram DM and someone ' +
          'standing at the counter — often the same day. Inikru treats all four as orders in ' +
          'one book, with one stock pool behind them.',
      },
    ],
    faqs: [
      {
        q: 'Do I need a developer to use it?',
        a: 'No. The storefront is built in a visual drag-and-drop editor, and everything else is configuration.',
      },
      {
        q: 'Can I use my own domain?',
        a: 'Yes. Connect any domain you own, or start on a free inikru.com subdomain and move later.',
      },
      {
        q: 'Is it only for ecommerce?',
        a: 'No. Businesses that sell mostly offline use it for billing, stock and customers, and switch the storefront on when they are ready.',
      },
    ],
    related: ['/online-store-builder', '/erp-software', '/shopify-alternative-india'],
  },

  '/online-store-builder': {
    eyebrow: 'Online store builder',
    h1: 'Create an online store in an afternoon',
    lede:
      'An online store builder for people who have a business to run. Pick a design, ' +
      'put your products in, connect payments, publish. No code, no developer, no ' +
      'six-week project.',
    hue: 'store',
    features: [
      {
        title: 'Start from a real design',
        body:
          'Dozens of complete store designs — not blank canvases. Every page is already ' +
          'laid out; you change the words and the pictures.',
      },
      {
        title: 'Edit what you can see',
        body:
          'Click any heading, image or button and change it in place. What you see while ' +
          'editing is what visitors get.',
      },
      {
        title: 'A catalogue that handles reality',
        body:
          'Sizes, colours, variants, per-size stock, discounts and collections — the things ' +
          'a real product range needs.',
      },
      {
        title: 'Ready to take money',
        body:
          'Checkout, shipping rules, coupons and order confirmation work from day one. ' +
          'Connect a payment account and you are selling.',
      },
    ],
    sections: [
      {
        title: 'What "no code" should mean',
        body:
          'It should mean you never see code — not that you get four colour choices and a ' +
          'logo slot. The visual editor lets you move sections, change layouts and restyle ' +
          'any element, so two shops using the same starting design do not end up looking ' +
          'like each other.',
      },
      {
        title: 'A digital storefront that grows up with you',
        body:
          'The same store that takes your first order handles the thousandth, with stock ' +
          'control, customer records and reports already attached.',
      },
    ],
    faqs: [
      {
        q: 'How long does it take to create an online store?',
        a: 'Most owners have a working store the same day. The slow part is usually photographs and product descriptions, not the software.',
      },
      {
        q: 'Can I change the design later?',
        a: 'Yes, at any time, without losing products, orders or customers.',
      },
      {
        q: 'What if I already sell on Instagram or WhatsApp?',
        a: 'Keep doing it. Those orders come into the same order book as website orders.',
      },
    ],
    related: ['/ecommerce-website-builder', '/ecommerce-platform', '/instagram-selling-platform'],
  },

  '/ecommerce-website-builder': {
    eyebrow: 'Ecommerce website builder',
    h1: 'An ecommerce website builder with no code anywhere',
    lede:
      'Build the whole website visually — home page, shop, product pages, policies, the lot. ' +
      'Drag sections around, edit text where it sits, publish when it looks right.',
    hue: 'store',
    features: [
      {
        title: 'Drag-and-drop, properly',
        body:
          'Add, reorder, duplicate and hide sections. Change layouts per device so the ' +
          'phone version is not an afterthought.',
      },
      {
        title: 'Every page is editable',
        body:
          'Not just the home page. Shop, product, collection, cart, about, contact and any ' +
          'custom page you invent.',
      },
      {
        title: 'Your fonts and colours',
        body:
          'A theme system that applies across the whole site at once, so the brand stays ' +
          'consistent without editing forty things.',
      },
      {
        title: 'Fast by default',
        body:
          'Images served from a CDN, pages cached, mobile layouts built in. Speed is a ' +
          'ranking factor and a conversion factor.',
      },
    ],
    sections: [
      {
        title: 'Website builder or ecommerce website builder?',
        body:
          'A general website builder gives you pages. An ecommerce website builder has to ' +
          'understand products, variants, stock, tax, shipping and checkout — and keep them ' +
          'correct while you redesign around them. Inikru is the second kind.',
      },
      {
        title: 'Build a website without a developer',
        body:
          'You do not need to know HTML, hosting, SSL or deployment. Publish is a button.',
      },
    ],
    faqs: [
      {
        q: 'Do I need hosting?',
        a: 'No. Hosting, SSL and the CDN are included and managed.',
      },
      {
        q: 'Can I add pages that are not shop pages?',
        a: 'Yes — lookbooks, size guides, journals, policy pages, anything.',
      },
      {
        q: 'Will it look like everyone else’s site?',
        a: 'Only if you leave it alone. Layouts, type and colour are all editable.',
      },
    ],
    related: ['/online-store-builder', '/boutique-website-builder', '/ecommerce-platform'],
  },

  '/erp-software': {
    eyebrow: 'ERP',
    h1: 'ERP software you can actually switch on',
    lede:
      'Cloud ERP without the twelve-month implementation. Inventory, purchases, production, ' +
      'GST and accounting, running behind the store you already have.',
    hue: 'stock',
    features: [
      {
        title: 'Inventory and purchasing',
        body:
          'Stock across locations, purchase orders, suppliers, batches, serial numbers and ' +
          'stock-take — with a real audit trail.',
      },
      {
        title: 'Production and materials',
        body:
          'Bills of material, item recipes and job tracking for businesses that make what ' +
          'they sell rather than only reselling it.',
      },
      {
        title: 'Accounting that posts itself',
        body:
          'Double-entry books built from the transactions you already record. Trial balance, ' +
          'P&L and balance sheet are always current.',
      },
      {
        title: 'GST and compliance',
        body:
          'GST invoices, GSTR-1 ready reports, e-invoice and e-way bill support where you need them.',
      },
    ],
    sections: [
      {
        title: 'Why most businesses never adopt ERP',
        body:
          'Traditional ERP assumes a project team, a consultant and a year. Most shops have ' +
          'none of those. Inikru turns each ERP capability into a feature you switch on when ' +
          'you need it, so the system grows with the business instead of arriving all at once.',
      },
      {
        title: 'Retail, fashion, jewellery, manufacturing',
        body:
          'The same core adapts: size-and-colour matrices for apparel, piece-level tracking ' +
          'for jewellery, serial numbers for electronics, batches and expiry for food.',
      },
    ],
    faqs: [
      {
        q: 'Is this a separate system from my store?',
        a: 'No. It is the same system. The store is one of the things the ERP runs.',
      },
      {
        q: 'Can I use the ERP without selling online?',
        a: 'Yes. Plenty of businesses use the billing, stock and accounting without publishing a storefront.',
      },
      {
        q: 'Does it handle GST?',
        a: 'Yes — GST invoices, tax invoices with GSTIN, credit notes and GSTR-1 ready exports.',
      },
    ],
    related: ['/inventory-management-software', '/gst-billing-software', '/retail-management-software'],
  },

  '/crm-software': {
    eyebrow: 'CRM',
    h1: 'CRM software that knows what people actually bought',
    lede:
      'A customer database that is wired to your orders, not typed in beside them. See what ' +
      'someone bought, when they last ordered, and what they are worth — then act on it.',
    hue: 'customers',
    features: [
      {
        title: 'One record per customer',
        body:
          'Orders, addresses, phone numbers, preferences and lifetime value in one place, ' +
          'built from real transactions.',
      },
      {
        title: 'Segments that mean something',
        body:
          'Group by what people bought, how often, how recently or how much — then talk to ' +
          'that group specifically.',
      },
      {
        title: 'Loyalty and vouchers',
        body:
          'Points, rewards and voucher campaigns that run against the same customer records.',
      },
      {
        title: 'Follow-ups that happen',
        body:
          'Reminders and campaigns so the customer who bought three months ago hears from ' +
          'you before they forget the name.',
      },
    ],
    sections: [
      {
        title: 'Customer relationship management for shops, not sales teams',
        body:
          'Most CRM software is built around a pipeline of deals and a team of reps. A retail ' +
          'or D2C business does not have those — it has thousands of small repeat ' +
          'relationships. This is customer management shaped for that.',
      },
      {
        title: 'Retention is cheaper than acquisition',
        body:
          'Knowing which customers are slipping away, and being able to reach exactly them, ' +
          'is usually worth more than another month of ads.',
      },
    ],
    faqs: [
      {
        q: 'Do I have to import my customers?',
        a: 'Only if you already have a list. Customers who order through your store are recorded automatically.',
      },
      {
        q: 'Does it work for offline customers too?',
        a: 'Yes. Counter sales and WhatsApp orders create the same customer records.',
      },
      {
        q: 'Can I export my customer data?',
        a: 'Yes. It is your data.',
      },
    ],
    related: ['/d2c-platform', '/whatsapp-order-management', '/retail-management-software'],
  },

  '/inventory-management-software': {
    eyebrow: 'Inventory',
    h1: 'Inventory management software that stays honest',
    lede:
      'Real-time stock across products, variants, sizes and locations — updated by the orders ' +
      'and bills you are already recording, so the count does not drift.',
    hue: 'stock',
    features: [
      {
        title: 'Variant-level stock',
        body:
          'Track by size and colour, not just by product. Sell out of one size without hiding ' +
          'the rest of the range.',
      },
      {
        title: 'Multiple locations',
        body:
          'Shop, warehouse and online stock in one view, with transfers between them.',
      },
      {
        title: 'Low-stock alerts',
        body:
          'Know before you sell something you cannot ship, and before your best line runs out.',
      },
      {
        title: 'Batches, serials and ageing',
        body:
          'Expiry batches for food, serial and IMEI tracking for electronics, ageing reports ' +
          'for anything that sits too long.',
      },
    ],
    sections: [
      {
        title: 'Stock control fails at the joins',
        body:
          'Inventory usually goes wrong not because the stock software is bad, but because ' +
          'the website, the counter and the stock register are three different systems that ' +
          'each learn about a sale at a different time. Sharing one database removes the join.',
      },
      {
        title: 'A stock take you can finish',
        body:
          'Count on a phone, record variances, post the adjustment. The books follow automatically.',
      },
    ],
    faqs: [
      {
        q: 'Does online stock update automatically?',
        a: 'Yes. An online order, a counter sale and a stock adjustment all move the same number.',
      },
      {
        q: 'Can I track stock without selling online?',
        a: 'Yes.',
      },
      {
        q: 'What about products I do not want to track?',
        a: 'Leave tracking off for those and they will never block a sale.',
      },
    ],
    related: ['/erp-software', '/order-management-software', '/retail-management-software'],
  },

  '/order-management-software': {
    eyebrow: 'Order management',
    h1: 'One order book for every way you sell',
    lede:
      'Website, WhatsApp, Instagram and the counter all land in the same place — with stock, ' +
      'invoices and payment status attached.',
    hue: 'sales',
    features: [
      {
        title: 'Every channel, one list',
        body:
          'Stop reconciling a website dashboard against a chat thread against a notebook.',
      },
      {
        title: 'A board you can work from',
        body:
          'Move orders through packing, dispatch and delivery, and see what is stuck.',
      },
      {
        title: 'Invoices and receipts',
        body:
          'Bills and GST tax invoices generate from the order — no re-typing, no mismatched totals.',
      },
      {
        title: 'Customers can track themselves',
        body:
          'Order tracking by phone number cuts the "where is my parcel" messages.',
      },
    ],
    sections: [
      {
        title: 'Order fulfilment is where growth breaks',
        body:
          'Twenty orders a week fit in your head. Two hundred do not. Order processing ' +
          'software earns its place the week you stop being able to remember what has shipped.',
      },
      {
        title: 'Pre-orders, quotes and counter sales',
        body:
          'Take an order before stock lands, quote a B2B buyer, or ring up a walk-in — all ' +
          'into the same system.',
      },
    ],
    faqs: [
      {
        q: 'Can I add an order manually?',
        a: 'Yes — quick add for phone, WhatsApp and walk-in orders.',
      },
      {
        q: 'Does it print invoices?',
        a: 'Yes, both customer bills and GST tax invoices.',
      },
      {
        q: 'Can staff use it without seeing everything?',
        a: 'Yes. Roles control who sees money, customers and settings.',
      },
    ],
    related: ['/whatsapp-order-management', '/inventory-management-software', '/gst-billing-software'],
  },

  '/gst-billing-software': {
    eyebrow: 'GST billing',
    h1: 'GST billing software wired to your orders',
    lede:
      'GST invoices, tax invoices with GSTIN, credit notes and GSTR-1 ready reports — ' +
      'generated from transactions you already recorded, not typed a second time.',
    hue: 'accounts',
    features: [
      {
        title: 'Compliant invoices',
        body:
          'Proper tax invoices with GSTIN, HSN, rate splits and totals that add up.',
      },
      {
        title: 'Credit notes and returns',
        body:
          'Handle a return the way the tax system expects, not with a scribbled adjustment.',
      },
      {
        title: 'GSTR-1 ready',
        body:
          'Reports laid out the way your filing needs them, exportable for your accountant.',
      },
      {
        title: 'One number everywhere',
        body:
          'The invoice, the order and the books agree, because they are the same record.',
      },
    ],
    sections: [
      {
        title: 'Billing software that is not a separate island',
        body:
          'Standalone GST billing software means entering every sale twice — once where it ' +
          'happened and once where it gets taxed. That second entry is where mismatches come ' +
          'from, and it is the entry nobody has time for at month end.',
      },
      {
        title: 'For your accountant',
        body:
          'Clean exports, a real trial balance and Tally-compatible output, so handover is a ' +
          'file rather than an argument.',
      },
    ],
    faqs: [
      {
        q: 'Can I issue non-GST bills too?',
        a: 'Yes. A tax invoice is issued when you have a GSTIN configured; otherwise customers get a plain bill.',
      },
      {
        q: 'Does it do e-invoice and e-way bills?',
        a: 'Yes, where your turnover requires them.',
      },
      {
        q: 'Can my accountant get the data out?',
        a: 'Yes — CSV exports and Tally XML.',
      },
    ],
    related: ['/erp-software', '/order-management-software', '/ecommerce-platform'],
  },

  '/shopify-alternative-india': {
    eyebrow: 'Comparison',
    h1: 'A Shopify alternative built for India',
    lede:
      'Shopify gives you an excellent storefront. Indian businesses then buy GST billing, ' +
      'inventory and CRM separately, and pay in dollars. Inikru puts them in one platform, ' +
      'priced in rupees.',
    hue: 'b2b',
    features: [
      {
        title: 'The storefront, included',
        body:
          'Branded online store, your own domain, a visual editor and a real product catalogue.',
      },
      {
        title: 'GST that is native, not an app',
        body:
          'Tax invoices, credit notes and GSTR-1 reports built in — not a third-party plugin ' +
          'with a monthly fee.',
      },
      {
        title: 'ERP and CRM in the same login',
        body:
          'Inventory, purchases, accounting and customer management, rather than four ' +
          'subscriptions that need to be wired together.',
      },
      {
        title: 'Payments to your own account',
        body:
          'UPI, cards and netbanking through your own gateway, settling to your bank.',
      },
    ],
    sections: [
      {
        title: 'What Shopify does better',
        body:
          'A larger app ecosystem, a bigger theme marketplace and a longer track record at ' +
          'very high scale. If your business depends on a specific app that only exists there, ' +
          'that is a real reason to stay — and worth checking before you move.',
      },
      {
        title: 'Where the difference shows up',
        body:
          'Usually at month end. When billing, stock and customers live in the same system as ' +
          'the storefront, the numbers reconcile themselves; when they do not, somebody spends ' +
          'a weekend on it.',
      },
      {
        title: 'Also compared with',
        body:
          'Businesses looking at Inikru are often also weighing Dukaan, Wix, Squarespace, ' +
          'WooCommerce, Ecwid and Instamojo. The honest summary: those are website or payment ' +
          'tools first, and back-office second — which is the opposite of how a growing shop ' +
          'runs out of road.',
      },
    ],
    faqs: [
      {
        q: 'Can I move an existing store across?',
        a: 'Yes. Products, customers and orders can be imported, and your domain points at the new store when you are ready.',
      },
      {
        q: 'Is my data locked in?',
        a: 'No. Products, customers, orders and books all export.',
      },
      {
        q: 'Do I pay in rupees?',
        a: 'Yes.',
      },
    ],
    related: ['/ecommerce-platform', '/d2c-platform', '/online-store-builder'],
  },

  '/d2c-platform': {
    eyebrow: 'D2C',
    h1: 'Sell direct, and actually own the relationship',
    lede:
      'A marketplace rents you customers. Inikru is a D2C platform: it gets your own ' +
      'branded website ready to sell, and runs the ERP and CRM behind it — so the ' +
      'customer, the brand and the margin are yours.',
    hue: 'customers',
    features: [
      {
        title: 'Your brand, end to end',
        body:
          'Your domain, your design, your packaging insert, your follow-up email. No other ' +
          'brand’s logo above your product.',
      },
      {
        title: 'Your customer list',
        body:
          'Contact details, order history and consent belong to you — the single most ' +
          'valuable thing a marketplace never hands over.',
      },
      {
        title: 'Your margin',
        body:
          'No marketplace commission on every order. You keep the difference and decide the price.',
      },
      {
        title: 'The unglamorous half',
        body:
          'Stock, invoicing, returns, accounting and support — the part that decides whether ' +
          'a D2C brand survives its own growth.',
      },
    ],
    sections: [
      {
        title: 'Direct to consumer is an operations problem',
        body:
          'The website is the easy part. What breaks a growing D2C brand is a hundred orders ' +
          'a day against stock nobody has counted, and a customer list spread across three ' +
          'tools. Branded ecommerce only works if the back office keeps up.',
      },
    ],
    faqs: [
      {
        q: 'Can I sell on marketplaces as well?',
        a: 'Yes. Many brands do both — the point is that the direct channel is yours.',
      },
      {
        q: 'Do I own the customer data?',
        a: 'Yes, and you can export it whenever you want.',
      },
      {
        q: 'How fast can I launch?',
        a: 'A working branded store the same day; the operations layer switches on as you need it.',
      },
    ],
    related: ['/ecommerce-platform', '/crm-software', '/instagram-selling-platform'],
  },

  '/boutique-website-builder': {
    eyebrow: 'Fashion & boutiques',
    h1: 'A website builder that understands clothing',
    lede:
      'Size runs, colour variants, fabric and care, lookbooks — plus the stock and billing to ' +
      'run the label behind the website.',
    hue: 'people',
    features: [
      {
        title: 'Sizes and colours done right',
        body:
          'Per-size stock, colour variants linked as one design, and size guides that fill ' +
          'themselves from measurements.',
      },
      {
        title: 'Fabric, care and fit',
        body:
          'The details clothing customers ask about before they buy — and return things over ' +
          'when they are missing.',
      },
      {
        title: 'Designs made for garments',
        body:
          'Store designs built around lookbooks and collections rather than generic product grids.',
      },
      {
        title: 'The label behind it',
        body:
          'Fabric stock, karigar and job tracking, and GST billing for the business side of ' +
          'the boutique.',
      },
    ],
    sections: [
      {
        title: 'Clothing inventory is a matrix, not a list',
        body:
          'One design is thirty SKUs once you multiply size by colour. Garment management ' +
          'software that treats it as a flat list will be wrong by the second week.',
      },
    ],
    faqs: [
      {
        q: 'Can I sell made-to-order pieces?',
        a: 'Yes — pre-orders and made-to-measure orders are supported alongside ready stock.',
      },
      {
        q: 'Does it handle a physical boutique too?',
        a: 'Yes. Counter sales share the same stock and customers as the website.',
      },
      {
        q: 'Can I show a lookbook?',
        a: 'Yes — editorial pages are part of the editor, not an add-on.',
      },
    ],
    related: ['/ecommerce-website-builder', '/jewellery-website-builder', '/inventory-management-software'],
  },

  '/jewellery-website-builder': {
    eyebrow: 'Jewellery',
    h1: 'Sell jewellery online without bending the software',
    lede:
      'Piece-level inventory, certificates, making charges and jeweller-shaped billing — ' +
      'on a storefront built to make the pieces look like they cost what they cost.',
    hue: 'sales',
    features: [
      {
        title: 'Every piece tracked individually',
        body:
          'One-of-a-kind items are not a quantity of five. Track each piece, with its own ' +
          'weight, certificate and photographs.',
      },
      {
        title: 'Billing jewellers recognise',
        body:
          'Making charges, wastage and metal rates handled in the invoice rather than worked ' +
          'around in a notes field.',
      },
      {
        title: 'Presentation that suits the price',
        body:
          'Designs built for high-value photography, close-up detail and slow, considered browsing.',
      },
      {
        title: 'Customers worth remembering',
        body:
          'Jewellery is a relationship business. Purchase history and follow-ups sit next to ' +
          'the catalogue.',
      },
    ],
    sections: [
      {
        title: 'Why generic ecommerce struggles here',
        body:
          'Most platforms assume interchangeable stock at a fixed price. Jewellery is unique ' +
          'pieces at a price that moves with the metal rate — jewellery management software ' +
          'has to start from that, not patch it on.',
      },
    ],
    faqs: [
      {
        q: 'Can I show certificates?',
        a: 'Yes, per piece.',
      },
      {
        q: 'Does it handle a showroom as well as online?',
        a: 'Yes — one stock pool, counter billing included.',
      },
      {
        q: 'Can prices follow the metal rate?',
        a: 'Yes, with price lists you control.',
      },
    ],
    related: ['/boutique-website-builder', '/erp-software', '/gst-billing-software'],
  },

  '/whatsapp-order-management': {
    eyebrow: 'WhatsApp commerce',
    h1: 'Turn WhatsApp messages into real orders',
    lede:
      'Most Indian businesses take orders on WhatsApp. Almost none can tell you what they sold ' +
      'last Tuesday. This closes that gap without changing how your customers talk to you.',
    hue: 'accounts',
    features: [
      {
        title: 'Capture the order once',
        body:
          'Quick-add an order from a chat, with the customer, items and address recorded properly.',
      },
      {
        title: 'Stock moves with it',
        body:
          'A WhatsApp sale reduces stock exactly like a website sale, so you stop overselling.',
      },
      {
        title: 'Send a payment link',
        body:
          'Collect by UPI or card without asking anyone to install anything.',
      },
      {
        title: 'Confirmations and updates',
        body:
          'Order confirmations and dispatch updates go back to the same chat.',
      },
    ],
    sections: [
      {
        title: 'The problem is not WhatsApp',
        body:
          'WhatsApp is the best sales channel most businesses have. The problem is that ' +
          'the record of the sale stays inside a chat thread, where it cannot be counted, ' +
          'taxed or reordered against.',
      },
    ],
    faqs: [
      {
        q: 'Do my customers need a new app?',
        a: 'No. They keep messaging you exactly as they do now.',
      },
      {
        q: 'Can I still send an invoice?',
        a: 'Yes — a proper bill or GST tax invoice from the same order.',
      },
      {
        q: 'Does this work alongside a website?',
        a: 'Yes. Both feed one order book and one stock pool.',
      },
    ],
    related: ['/order-management-software', '/instagram-selling-platform', '/crm-software'],
  },

  '/instagram-selling-platform': {
    eyebrow: 'Instagram commerce',
    h1: 'Give your Instagram audience somewhere to buy',
    lede:
      'A link-in-bio that is a real store — catalogue, checkout, stock and order management ' +
      'behind it — instead of a DM asking for a price.',
    hue: 'people',
    features: [
      {
        title: 'A storefront for the bio link',
        body:
          'Your whole catalogue, browsable and buyable, on a link that fits in a profile.',
      },
      {
        title: 'Stop answering "price?"',
        body:
          'Prices, sizes and availability are on the page, so the DMs are about the things ' +
          'only you can answer.',
      },
      {
        title: 'Orders in one book',
        body:
          'Instagram, WhatsApp and website orders land together, against one stock pool.',
      },
      {
        title: 'Keep the audience you built',
        body:
          'Customers who buy become customer records you own — not followers on a platform ' +
          'that changes its algorithm.',
      },
    ],
    sections: [
      {
        title: 'The gap between interest and purchase',
        body:
          'An Instagram post creates the intent. If the next step is a DM, a price quote and ' +
          'a UPI screenshot, most of that intent evaporates overnight. A working checkout is ' +
          'the shortest path between the two.',
      },
    ],
    faqs: [
      {
        q: 'Do I need a lot of products?',
        a: 'No. A handful is enough to be worth a proper link.',
      },
      {
        q: 'Can I keep selling through DMs?',
        a: 'Yes — those orders can be added to the same system.',
      },
      {
        q: 'Can I use my own domain?',
        a: 'Yes.',
      },
    ],
    related: ['/whatsapp-order-management', '/d2c-platform', '/online-store-builder'],
  },

  '/retail-management-software': {
    eyebrow: 'Retail',
    h1: 'Retail management software for one shop or five',
    lede:
      'Counter sales, stock, customers, billing and reports in one system — whether you sell ' +
      'across a counter, across the country, or both on the same day.',
    hue: 'b2b',
    features: [
      {
        title: 'Counter and online, one business',
        body:
          'A walk-in sale and a website order draw from the same stock and build the same ' +
          'customer history.',
      },
      {
        title: 'Multiple locations',
        body:
          'Per-branch stock, transfers and reporting, with staff who only see their own.',
      },
      {
        title: 'Staff with roles',
        body:
          'Give people the screens they need and none of the ones they do not. Every change ' +
          'is recorded.',
      },
      {
        title: 'Reports you will read',
        body:
          'What sold, what is dead stock, who buys most, what you actually made — daily, ' +
          'without exporting anything.',
      },
    ],
    sections: [
      {
        title: 'A business operating system, not a till',
        body:
          'A billing machine records sales. Business management software connects that sale ' +
          'to stock, to the customer, to the books and to what you should order next — which ' +
          'is where the time is actually saved.',
      },
    ],
    faqs: [
      {
        q: 'Does it work offline at the counter?',
        a: 'It is a cloud system and needs a connection; billing is designed to be fast on a normal shop internet line.',
      },
      {
        q: 'Can I start with just billing?',
        a: 'Yes. Turn on stock, customers and accounting when you want them.',
      },
      {
        q: 'Is there a limit on staff logins?',
        a: 'Staff logins with roles are supported; seat limits depend on your plan.',
      },
    ],
    related: ['/erp-software', '/inventory-management-software', '/gst-billing-software'],
  },
};

export const SEO_PATHS = Object.keys(SEO_PAGES);
